import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import type { I18nJsonb } from "./i18n";
import { district, province, shippingCarrier } from "./shipping";

/** กลุ่มลูกค้า (Customer groups) — many-to-many กับ customer */
export const customerGroup = pgTable("customer_group", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: jsonb("name").$type<I18nJsonb>().notNull(),
  description: text("description"),
  color: text("color"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
});

/** ระดับสมาชิก (Member tiers) */
export const memberTier = pgTable("member_tier", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: jsonb("name").$type<I18nJsonb>().notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
});

/**
 * ลูกค้า (front-end / สมาชิก) — ใช้ user ร่วม
 * Identity (email, name, phone) อ่านจาก user; เก็บเฉพาะ member tier + metadata
 */
export const customer = pgTable(
  "customer",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    memberTierId: text("member_tier_id").references(() => memberTier.id, {
      onDelete: "set null",
    }),
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    unique("customer_user_id_unique").on(t.userId),
    index("customer_user_id").on(t.userId),
    index("customer_member_tier_id").on(t.memberTierId),
  ],
);

/**
 * ที่อยู่จัดส่งของลูกค้า (บันทึกที่อยู่) — ใช้ตอน checkout และในโปรไฟล์
 */
export const customerAddress = pgTable(
  "customer_address",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    customerId: text("customer_id")
      .notNull()
      .references(() => customer.id, { onDelete: "cascade" }),
    /** ชื่อที่อยู่ เช่น "บ้าน" / "ที่ทำงาน" */
    label: text("label"),
    recipientName: text("recipient_name").notNull(),
    phone: text("phone").notNull(),
    /** ບ້ານ (village) */
    village: text("village").notNull(),
    provinceId: text("province_id")
      .notNull()
      .references(() => province.id, { onDelete: "restrict" }),
    districtId: text("district_id")
      .notNull()
      .references(() => district.id, { onDelete: "restrict" }),
    /** ผู้ให้บริการขนส่งที่ลูกค้าเลือก (nullable = ยังไม่ระบุ) */
    shippingCarrierId: text("shipping_carrier_id").references(
      () => shippingCarrier.id,
      { onDelete: "set null" },
    ),
    /** สาขาผู้ให้บริการ (ข้อความ) */
    carrierBranch: text("carrier_branch"),
    isDefault: boolean("is_default").notNull().default(false),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("customer_address_customer_id").on(t.customerId),
    index("customer_address_is_default").on(t.customerId, t.isDefault),
  ],
);

/** Junction: ลูกค้า ↔ กลุ่ม (many-to-many) */
export const customerGroupMember = pgTable(
  "customer_group_member",
  {
    customerId: text("customer_id")
      .notNull()
      .references(() => customer.id, { onDelete: "cascade" }),
    groupId: text("group_id")
      .notNull()
      .references(() => customerGroup.id, { onDelete: "cascade" }),
    assignedAt: timestamp("assigned_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    assignedBy: text("assigned_by").references(() => user.id, {
      onDelete: "set null",
    }),
  },
  (t) => [
    primaryKey({ columns: [t.customerId, t.groupId] }),
    index("customer_group_member_group_id").on(t.groupId),
  ],
);
